"use client";

import "@/components/home/features-content.css";
import "@/components/ui/molecules/categories/page-chrome.css";
import { FeaturesCrawl } from "@/components/home/features-crawl";

import { FigureCaptionLine } from "@/components/home/figure-caption";
import { SectionKicker } from "@/components/home/section-kicker";
import { SiteFooter } from "@/components/home/site-footer";
import { SiteNav } from "@/components/home/site-nav";
import { LoopVideo } from "@/components/ui/atoms/LoopVideo";
import { heroSerif } from "@/lib/theme/hero-serif";
import Image from "next/image";

import { memo } from "react";

const PILLARS: {
  id: string;
  title: string;
  note: string;
  headline: string;
  figcaption: string;
  body: string;
  art: string;
  shot?: {
    src: string;
    width: number;
    height: number;
    alt: string;
  };
  video?: {
    src: string;
    poster?: string;
    alt: string;
  };
  shotLabel: string;
  cards: {
    title: string;
    body: string;
    video?: {
      src: string;
      alt: string;
    };
    shot?: {
      src: string;
      width: number;
      height: number;
      alt: string;
    };
  }[];
}[] = [
  {
    id: "fluid",
    title: "Fluid",
    note: "The intelligence layer",
    headline: "The AI that can run your whole business.",
    figcaption: "Fig. 1 · Asking Fluid who's ready, live",
    body: "This one is our personal favorite. Fluid can run your entire business. It has access to every end of the software, from onboarding to importing reports, disputing, scheduling disputes, and billing, and it can even tell you about your day and what needs to happen next. Ask it \"what should we dispute next?\" and it answers from your actual client data. Every move it makes runs on Applied Metro 2™, our standardized dispute methodology.",
    art: "/graphics/dw-burn-bg.webp",
    video: {
      src: "/videos/dw-fluid-features.mp4",
      poster: "/graphics/dw-fluid-features-poster.jpg",
      alt: "Asking Fluid which clients are ready for their first round: it searches, runs the action, and answers",
    },
    shotLabel: "fluid",
    cards: [
      {
        title: "Auto-Prepare",
        body: "Fluid can prepare a full round end to end. Items selected, addresses resolved, letters drafted, queued for review.",
        video: {
          src: "/videos/dw-fluid-ask.mp4",
          alt: "Asking Fluid to schedule disputes for a client, live in the app",
        },
      },
      {
        title: "Runs on Schedule",
        body: "Enroll a client once and Fluid preps each new round on cadence, or the moment a fresh report lands.",
        shot: {
          src: "/graphics/dw-shot-fluid-schedule.png",
          width: 3024,
          height: 1646,
          alt: "Asking Fluid to schedule disputes for a client: it loads the record and confirms round five, ready for automation",
        },
      },
      {
        title: "Only what you approve",
        body: "By default, you confirm everything. Nothing mails until you hit approve. Now, we have a yolo mode for when you trust fluid entirely and want to let it do absolutely everything for you.",
      },
      {
        title: "Learns from Outcomes",
        body: "Fluid records predicted against realized score gains and recalibrates for your business every round.",
      },
    ],
  },
  {
    id: "disputes",
    title: "Dispute",
    note: "Applied Metro 2™",
    headline: "Build a full round in one step.",
    figcaption: "Fig. 2 · Round 6 in progress with Fluid open",
    body: "Dispute lets you run the entire dispute process as one step. Pick the items and it builds compliant letters with the right reasons, instructions, and creditor addresses, ready to mail. Underneath is Applied Metro 2™, our standardized methodology: 48 compliance checks on every account, the right law cited for every claim, and a clear plan for how each round escalates.",
    art: "/graphics/dw-spectrum-bg.webp",
    shot: {
      src: "/graphics/dw-shot-dispute-rounds.png",
      width: 3164,
      height: 1772,
      alt: "A client's Disputes tab in dark mode with Round 6 selected, bureau score changes, tradeline rows, and the Fluid assistant open beside the round timeline",
    },
    shotLabel: "rounds workspace",
    cards: [
      {
        title: "Applied Metro 2™",
        body: "Every account gets checked against 48 compliance rules from the 2023 CRRG, the manual the bureaus themselves report by.",
      },
      {
        title: "Prints and Mails",
        body: "Letters print, ship USPS, and report delivery back into the timeline. You never touch an envelope.",
      },
      {
        title: "All Three Bureaus",
        body: "Equifax, Experian, and TransUnion, side by side every round.",
      },
      {
        title: "Escalates on Round Two",
        body: "If a bureau claims it verified something, the next letter demands proof of how. If they go silent, the next letter demands deletion. If an item sneaks back on, they get a formal reinsertion notice.",
      },
    ],
  },
  {
    id: "insights",
    title: "Dispute Insights",
    note: "Down to the account",
    headline: "Know exactly what moved the score.",
    figcaption: "Fig. 3 · The causality graph",
    body: "Every account, every methodology, every round leaves a trace. DisputeWell links dispute reason to account to bureau to outcome to score change, and Fluid reads the chain back to you in plain English. Every round stays on the record, so you can go back months later and review exactly what was disputed, with the reasons, instructions, and strategy behind it.",
    art: "/graphics/dw-walker-full.webp",
    shot: {
      src: "/graphics/dw-shot-insights.png",
      width: 1920,
      height: 1080,
      alt: "The causality graph: a traced chain from dispute reason through account, bureau, and round to a verified outcome and score change",
    },
    shotLabel: "causality graph",
    cards: [
      {
        title: "Strategy Narratives",
        body: "Fluid writes the round's reasoning in plain English, so you can read the why before you approve, and any time after.",
      },
      {
        title: "Round Diffs",
        body: "Every new report diffed against the last. Deleted, updated, verified, reinserted, per bureau.",
      },
      {
        title: "Account History Ledger",
        body: "Every dispute an account has ever seen, with responses and status changes in one place.",
      },
      {
        title: "Per-Item Analysis",
        body: "Every account gets analyzed against consumer protection law and scored for how disputable it really is.",
      },
    ],
  },
  {
    id: "crm",
    title: "The CRM",
    note: "The workhorse layer",
    headline: "Everything in one place.",
    figcaption: "Fig. 4 · The command center",
    body: "The CRM sees every client, lead, task, and conversation. It can always see the round each client is on, so the dashboard shows every score and every dollar without you asking.",
    art: "/graphics/dw-desk-hero.webp",
    shot: {
      src: "/graphics/dw-shot-dashboard.png",
      width: 1920,
      height: 1080,
      alt: "The main dashboard: a morning briefing of what happened yesterday and what needs you today, headline stats for clients and revenue, the business growth chart, and the command center listing every client's next action",
    },
    shotLabel: "dashboard",
    cards: [
      {
        title: "Leads to Clients",
        body: "Capture leads from public intake and convert them in one click. Bulk actions for everything else.",
      },
      {
        title: "Messages and SMS",
        body: "One inbox for every client conversation, internal and SMS, with unread counts.",
      },
      {
        title: "Drip Campaigns",
        body: "Write a sequence once and it sends from your own domain, on schedule.",
      },
      {
        title: "Attributes Outcomes",
        body: "See which disputes actually moved the score, down to the item.",
      },
    ],
  },
  {
    id: "billing",
    title: "Client billing",
    note: "Get paid",
    headline: "Billing runs itself.",
    figcaption: "Fig. 5 · Invoices and subscriptions",
    body: "Connect Authorize.net or NMI and billing takes care of itself. Cards are stored by token, plans recur weekly or monthly, and clients pay right inside the portal.",
    art: "/graphics/dw-runner-hero.webp",
    shot: {
      src: "/graphics/dw-shot-billing.png",
      width: 1920,
      height: 1080,
      alt: "A client's Billing tab: an active $129 monthly subscription, total paid and outstanding balances, a tokenized card on file, and the recurring plan with its next bill date",
    },
    shotLabel: "client billing",
    cards: [
      {
        title: "Recurring Plans",
        body: "Set a plan per client and it charges on schedule, retrying failures automatically.",
      },
      {
        title: "Bulk Charge",
        body: "Charge every outstanding invoice in one action.",
      },
      {
        title: "Portal Payments",
        body: "Clients add cards and pay invoices themselves. Tokenized, never stored raw.",
      },
      {
        title: "Guardrails",
        body: "You decide who can charge and how much. By default, agents can't.",
      },
    ],
  },
  {
    id: "compliance",
    title: "Compliance",
    note: "Table stakes",
    headline: "Compliance you can point to.",
    figcaption: "Fig. 6 · The 50-state dashboard",
    body: "DisputeWell tracks what a regulated business has to prove. Evidence and contract term limits for all 50 states, CROA agreements that compute their own cancellation deadlines, and an audit trail behind every sensitive action.",
    art: "/graphics/dw-flower-spectrum.webp",
    shot: {
      src: "/graphics/dw-shot-compliance.png",
      width: 1920,
      height: 1080,
      alt: "The 50-state compliance dashboard: a US map shaded by readiness with per-state client counts, and the Readiness by State table tracking bond, license, registration, and contract limits",
    },
    shotLabel: "compliance dashboard",
    cards: [
      {
        title: "50-State Tracking",
        body: "Evidence readiness and contract term limits, state by state.",
      },
      {
        title: "CROA Built In",
        body: "Required agreements and disclosures, with the cancellation deadline computed and stamped in.",
      },
      {
        title: "Audit Trail",
        body: "Every sensitive action is logged with who, what, when, and before-and-after.",
      },
      {
        title: "Five Roles",
        body: "Owner, manager, agent, outsourcer, client. Each sees exactly what you allow.",
      },
    ],
  },
  {
    id: "migration",
    title: "Migration",
    note: "Bring everything",
    headline: "Switch without starting over.",
    figcaption: "Fig. 7 · A DisputeFox migration, live",
    body: "Point DisputeWell at your DisputeFox account and it migrates your clients while you watch. Or drop a CSV. Try it out from Contacts, then Import.",
    art: "/graphics/dw-terminal-art.webp",
    video: {
      src: "/videos/dw-migration-features.mp4",
      poster: "/graphics/dw-migration-features-poster.jpg",
      alt: "A DisputeFox migration running live: clients imported from DisputeFox, parsed, and added to DisputeWell while the progress panel streams each step",
    },
    shotLabel: "disputefox import",
    cards: [
      {
        title: "DisputeFox Migration",
        body: "Enter your instance URL and credentials. Clients, reports, and history come over with live progress.",
      },
      {
        title: "CSV Wizard",
        body: "Up to 5,000 rows with column mapping, duplicate policy, and a full error report.",
      },
      {
        title: "Monitoring Providers",
        body: "SmartCredit and Credit Karma out of the box. More on request.",
      },
      {
        title: "Resumes on OTP",
        body: "Report pulls pause for one-time codes and pick up right where they left off.",
      },
    ],
  },
  {
    id: "white-label",
    title: "White-label",
    note: "Your brand, not ours",
    headline: "Your name on everything.",
    figcaption: "Fig. 8 · The brand studio",
    body: "Your logo, your colors, your domain. Clients only ever see your brand, in the dashboard, the portal, and onboarding. Other companies charge thousands for this. It's included.",
    art: "/graphics/dw-iso-bg.webp",
    shot: {
      src: "/graphics/dw-shot-branding.webp",
      width: 2048,
      height: 1113,
      alt: "The Branding settings screen: logo slots, credit score gauge styles, and a grid of theme presets",
    },
    shotLabel: "branding",
    cards: [
      {
        title: "Client Portal",
        body: "Clients sign in at your domain, watch their scores climb, and pay their invoices.",
      },
      {
        title: "Onboarding Builder",
        body: "Design the steps, fields, agreements, e-sign, and payments yourself.",
      },
      {
        title: "Custom Domains",
        body: "portal.yourbrand.com, provisioned automatically. A DNS wizard verifies it in minutes.",
      },
      {
        title: "Login Page Designer",
        body: "Design the front door. Preview it live on a phone and a desktop, then publish.",
      },
    ],
  },
];

export const FeaturesContent = memo(function FeaturesContent() {
  return (
    <div
      className={heroSerif.variable} data-component="FeaturesContent" data-location="src/components/home/features-content.tsx">
      <SiteNav />

      <main id="main-content">
        <section className="dw-feat__hero" aria-label="Features">
          <h1 className="dw-text-hero-lg" data-tone="foreground">Features.</h1>
          <p className="dw-text-body dw-tabular-nums" data-tone="muted">
            The full-stack credit repair platform.
          </p>
        </section>

        {PILLARS.map((pillar, index) => (
          <section
            key={pillar.id}
            className="dw-feat__pillar dw-home__stack"
            aria-label={pillar.title}
          >
            <SectionKicker
              numeral={`0${index + 1}`}
              label={pillar.title}
              note={pillar.note}
            />
            <h2 className="dw-home__headline dw-text-hero" data-tone="foreground">
              {pillar.headline}
            </h2>
            <div className="dw-feat__showcase">
            <figure className="dw-feat__figure">
              <FigureCaptionLine>{pillar.figcaption}</FigureCaptionLine>
              <article className="dw-feat__card dw-marketing-card">
                <Image
                  src={pillar.art}
                  alt=""
                  aria-hidden
                  fill
                  sizes="100vw"
                  className="dw-feat__card-art"
                />
                <div className="dw-feat__card-copy">
                  <h3 className="dw-text-hero">{pillar.title}</h3>
                  <p className="dw-text-body dw-tabular-nums">{pillar.body}</p>
                </div>
                <div className="dw-feat__card-media">
                  {pillar.video ? (
                    <LoopVideo
                      className="dw-feat__card-shot"
                      src={pillar.video.src}
                      poster={pillar.video.poster}
                      label={pillar.video.alt}
                    />
                  ) : pillar.shot ? (
                    <Image
                      src={pillar.shot.src}
                      alt={pillar.shot.alt}
                      width={pillar.shot.width}
                      height={pillar.shot.height}
                      sizes="(min-width: 1024px) 60vw, 95vw"
                      className="dw-feat__card-shot"
                    />
                  ) : (
                    <span className="dw-feat__card-placeholder dw-text-body dw-tabular-nums">
                      [ shot — {pillar.shotLabel} ]
                    </span>
                  )}
                </div>
              </article>
            </figure>

            <ul className="dw-feat__grid">
              {pillar.cards.map((card) => (
                <li key={card.title} className="dw-feat__mini dw-panel" data-variant="muted" data-padding="lg">
                  {(card.video || card.shot) && (
                    <div className="dw-feat__mini-media">
                      {card.video ? (
                        <LoopVideo
                          className="dw-feat__mini-video"
                          src={card.video.src}
                          label={card.video.alt}
                        />
                      ) : card.shot ? (
                        <Image
                          src={card.shot.src}
                          alt={card.shot.alt}
                          width={card.shot.width}
                          height={card.shot.height}
                          sizes="(min-width: 1024px) 35rem, 90vw"
                          className="dw-feat__mini-video"
                        />
                      ) : null}
                    </div>
                  )}
                  <div className="dw-feat__mini-copy">
                    <h3 className="dw-text-heading">{card.title}</h3>
                    <p className="dw-text-body dw-tabular-nums">{card.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            </div>
          </section>
        ))}

        <FeaturesCrawl />
      </main>

      <SiteFooter />
    </div>
  );
});
